"use client";

import { motion } from "framer-motion";
import Image from "next/image";
import { cn } from "@/lib/utils";

const testimonials = [
	{
		quote:
			"Financial indicators and milestone tracking have saved us precious time.",
		name: "Mohamed ROUTAK",
		company: "Director, BLUE HOME",
		avatar: "/placeholders/blue-home.png",
		type: "small-dark",
	},
	{
		quote:
			"Team coordination has become smoother and our deadlines have dropped by 20%.",
		name: "Taoufiq LAARIF",
		company: "Director, TL GLOBAL",
		avatar: "/placeholders/tl-global.png",
		type: "small-dark",
	},
	{
		quote:
			"BRIK has transformed the way we manage our projects. The visibility it provides is unparalleled.",
		name: "Annette Black",
		company: "HouseIN",
		avatar: "/placeholders/blue-home.png",
		type: "small-dark",
	},
];

const TestimonialCard = ({
	quote,
	name,
	company,
	avatar,
	type,
}: {
	quote: string;
	name: string;
	company: string;
	avatar: string;
	type: string;
}) => {
	const isLargeCard = type.startsWith("large");
	const avatarSize = isLargeCard ? 48 : 36;
	const avatarBorderRadius = isLargeCard
		? "rounded-[41px]"
		: "rounded-[30.75px]";
	const padding = isLargeCard ? "p-6" : "p-[30px]";

	let cardClasses = `flex flex-col justify-between items-start overflow-hidden rounded-[10px] shadow-sm relative ${padding}`;
	let quoteClasses = "";
	let nameClasses = "";
	let companyClasses = "";
	const backgroundElements = null;
	let cardHeight = "";
	const cardWidth = "w-full";

	if (type === "large-teal") {
		cardClasses += " bg-primary text-primary-foreground";
		quoteClasses += " text-2xl font-medium leading-8";
		nameClasses += " text-base font-normal leading-6";
		companyClasses +=
			" text-primary-foreground/60 text-base font-normal leading-6";
		cardHeight = "h-auto md:h-[502px]";
	} else if (type === "large-light") {
		cardClasses += " bg-card";
		quoteClasses += " text-foreground text-2xl font-medium leading-8";
		nameClasses += " text-foreground text-base font-normal leading-6";
		companyClasses += " text-muted-foreground text-base font-normal leading-6";
		cardHeight = "h-auto md:h-[502px]";
	} else {
		// small-dark
		cardClasses += " bg-card";
		quoteClasses += " text-foreground text-lg font-normal leading-7";
		nameClasses += " text-foreground text-sm font-normal leading-5";
		companyClasses += " text-muted-foreground text-sm font-normal leading-5";
		cardHeight = "h-auto md:h-[235px]";
	}

	return (
		<div className={cn(cardWidth, cardHeight, cardClasses)}>
			{backgroundElements}
			<div className="relative z-10 flex flex-col h-full">
				<p className={cn(quoteClasses, "flex-grow")}>"{quote}"</p>
				<div className="flex items-center gap-4 mt-4">
					<Image
						src={avatar}
						alt={name}
						width={avatarSize}
						height={avatarSize}
						className={cn("object-cover", avatarBorderRadius)}
					/>
					<div>
						<p className={nameClasses}>{name}</p>
						<p className={companyClasses}>{company}</p>
					</div>
				</div>
			</div>
		</div>
	);
};

const fadeUp = {
	hidden: { opacity: 0, y: 24 },
	show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

export const TestimonialGridSection = () => {
	return (
		<motion.div
			initial="hidden"
			whileInView="show"
			viewport={{ once: true, margin: "-100px" }}
			variants={{
				show: {
					transition: {
						staggerChildren: 0.15,
					},
				},
			}}
			className="grid grid-cols-1 md:grid-cols-3 gap-4"
		>
			{testimonials.map((testimonial) => (
				<motion.div key={testimonial.name} variants={fadeUp} className="flex">
					<TestimonialCard {...testimonial} />
				</motion.div>
			))}
		</motion.div>
	);
};
